<div class="colimind-panel-header">
  <p class="panel-label">{{ 'colimind.settings' | translate }}</p>
</div>

<div class="colimind-panel-content colimind-settings-form">

  <!-- Profile: edited as a draft, written on Save -->
  <section class="colimind-settings-card">
    <label class="colimind-settings-label" for="setting-addressing">
      {{ 'colimind.settingAddressing' | translate }}
    </label>
    <input
      id="setting-addressing"
      class="colimind-settings-input"
      type="text"
      [ngModel]="draftAddressing()"
      (ngModelChange)="draftAddressing.set($event)"
      [maxlength]="maxAddressingLength"
      [placeholder]="'colimind.settingAddressingPlaceholder' | translate"
      />

    @if (instructionsAvailable || debug.enabled()) {
      <label
        class="colimind-settings-label"
        [class.colimind-settings-label--disabled]="!instructionsAvailable"
        for="setting-system-prompt"
        >
        {{ 'colimind.settingSystemPrompt' | translate }}
      </label>
      <textarea
        id="setting-system-prompt"
        class="colimind-settings-input colimind-settings-input--prompt"
        rows="12"
        [disabled]="!instructionsAvailable"
        [ngModel]="draftInstructions()"
        (ngModelChange)="draftInstructions.set($event)"
        [placeholder]="'colimind.settingSystemPromptPlaceholder' | translate"
      ></textarea>

      <div class="colimind-settings-meta">
        <span
          class="colimind-settings-counter"
          [class.colimind-settings-counter--over]="instructionsTooLong()"
          >{{ instructionsLength() }} / {{ maxSystemPromptLength }}</span>

        <label class="colimind-settings-toggle" [class.colimind-settings-toggle--disabled]="!instructionsAvailable">
          <input
            type="checkbox"
            [disabled]="!instructionsAvailable"
            [ngModel]="settings.settings().systemPromptActive"
            (ngModelChange)="settings.update({ systemPromptActive: $event })"
            />
          {{ 'colimind.settingSystemPromptActive' | translate }}
        </label>
      </div>

      @if (instructionsAvailable) {
        <p class="colimind-settings-hint">{{ 'colimind.settingsHint' | translate }}</p>
      } @else {
        <p class="colimind-unavailable colimind-instructions-unavailable">
          <mat-icon>schedule</mat-icon>
          {{ 'colimind.notAvailableYet' | translate }}
        </p>
      }

      @if (instructionsTooLong()) {
        <p class="colimind-settings-error">
          {{ 'colimind.settingSystemPromptTooLong' | translate: { max: maxSystemPromptLength } }}
        </p>
      }
    }

    <div class="colimind-settings-actions">
      <button
        type="button"
        class="colimind-btn colimind-btn--ghost"
        [disabled]="!dirty()"
        (click)="cancel()"
        >
        {{ 'colimind.cancel' | translate }}
      </button>
      <button
        type="button"
        class="colimind-btn colimind-btn--primary"
        [disabled]="!canSave()"
        (click)="save()"
        >
        {{ 'colimind.save' | translate }}
      </button>
    </div>
  </section>

  <!-- Prompt suggestions -->
  <section class="colimind-suggestions-section">
    <div class="colimind-suggestions-header">
      <p class="colimind-suggestions-title">{{ 'colimind.promptSuggestions' | translate }}</p>
      <button
        type="button"
        class="colimind-btn colimind-btn--primary"
        [disabled]="!settings.canAddSuggestion() || addingSuggestion()"
        (click)="startAddingSuggestion()"
        >
        <mat-icon>add</mat-icon>
        {{ 'colimind.addSuggestion' | translate }}
      </button>
    </div>

    <p class="colimind-suggestions-hint">{{ 'colimind.promptSuggestionsHint' | translate }}</p>

    @if (!settings.canAddSuggestion()) {
      <p class="colimind-suggestion-max-hint">{{ 'colimind.suggestionMaxReached' | translate }}</p>
    }

    <div class="colimind-suggestion-list">
      @for (def of defaultSuggestions; track def.id) {
        <div class="colimind-suggestion-row" [class.colimind-suggestion-row--inactive]="!defaultActive(def.id)">
          <span class="colimind-suggestion-icon"><mat-icon>{{ def.icon }}</mat-icon></span>
          <div class="colimind-suggestion-text">
            <p class="colimind-suggestion-title">{{ def.titleKey | translate }}</p>
            <p class="colimind-suggestion-instruction">{{ def.descKey | translate }}</p>
          </div>
          <span class="colimind-suggestion-badge">{{ 'colimind.defaultSuggestion' | translate }}</span>
          <button
            type="button"
            class="colimind-suggestion-toggle"
            [class.colimind-suggestion-toggle--on]="defaultActive(def.id)"
            [matTooltip]="(defaultActive(def.id) ? 'colimind.deactivateSuggestion' : 'colimind.activateSuggestion') | translate"
            (click)="settings.toggleDefault(def.id)"
            >
            <mat-icon>{{ defaultActive(def.id) ? 'check' : 'add' }}</mat-icon>
          </button>
        </div>
      }

      @for (sug of settings.suggestions(); track sug.id) {
        <div class="colimind-suggestion-row" [class.colimind-suggestion-row--inactive]="!sug.active">
          <span class="colimind-suggestion-icon"><mat-icon>lightbulb</mat-icon></span>
          <div class="colimind-suggestion-text">
            <p class="colimind-suggestion-title">{{ sug.title }}</p>
            <p class="colimind-suggestion-instruction">{{ sug.instruction }}</p>
          </div>
          <button
            type="button"
            class="colimind-suggestion-delete"
            [matTooltip]="'colimind.deleteSuggestion' | translate"
            (click)="settings.removeSuggestion(sug.id)"
            >
            <mat-icon>delete_outline</mat-icon>
          </button>
          <button
            type="button"
            class="colimind-suggestion-toggle"
            [class.colimind-suggestion-toggle--on]="sug.active"
            [matTooltip]="(sug.active ? 'colimind.deactivateSuggestion' : 'colimind.activateSuggestion') | translate"
            (click)="settings.toggleSuggestion(sug.id)"
            >
            <mat-icon>{{ sug.active ? 'check' : 'add' }}</mat-icon>
          </button>
        </div>
      }

      @if (addingSuggestion()) {
        <div class="colimind-suggestion-card colimind-suggestion-card--draft">
          <input
            #draftTitleInput
            class="colimind-suggestion-input"
            type="text"
            [ngModel]="settings.draftTitle()"
            (ngModelChange)="settings.draftTitle.set($event)"
            [placeholder]="'colimind.suggestionTitlePlaceholder' | translate"
            [maxlength]="maxTitleLength"
            />
          <textarea
            class="colimind-suggestion-input colimind-suggestion-input--textarea"
            rows="3"
            [ngModel]="settings.draftInstruction()"
            (ngModelChange)="settings.draftInstruction.set($event)"
            [placeholder]="'colimind.suggestionInstructionPlaceholder' | translate"
            [maxlength]="maxInstructionLength"
          ></textarea>
          <div class="colimind-settings-actions">
            <button
              type="button"
              class="colimind-btn colimind-btn--ghost"
              (click)="cancelAddingSuggestion()"
              >
              {{ 'colimind.cancel' | translate }}
            </button>
            <button
              type="button"
              class="colimind-btn colimind-btn--primary"
              [disabled]="!settings.draftTitle().trim() || !settings.draftInstruction().trim()"
              (click)="addSuggestion()"
              >
              {{ 'colimind.save' | translate }}
            </button>
          </div>
        </div>
      }
    </div>
  </section>

  <!--
    Schedules are still on the backlog as Won't/V1, so the section only shows in debug mode, and
    deliberately without example entries: a routine that looks real but does nothing would claim
    something the product cannot do yet.
  -->
  @if (debug.enabled()) {
    <section class="colimind-schedules-section">
      <div class="colimind-suggestions-header">
        <p class="colimind-suggestions-title">{{ 'colimind.schedules' | translate }}</p>
        <button type="button" class="colimind-btn colimind-btn--primary" disabled>
          <mat-icon>add</mat-icon>
          {{ 'colimind.addAction' | translate }}
        </button>
      </div>
      <p class="colimind-suggestions-hint">{{ 'colimind.schedulesHint' | translate }}</p>
      <p class="colimind-unavailable">
        <mat-icon>schedule</mat-icon>
        {{ 'colimind.notAvailableYet' | translate }}
      </p>
    </section>
  }

  <div class="colimind-settings-footer">
    <button
      type="button"
      class="colimind-btn colimind-btn--ghost colimind-settings-replay"
      (click)="settings.replayOnboarding()"
      >
      <mat-icon>replay</mat-icon>
      {{ 'colimind.onboardingReplay' | translate }}
    </button>
  </div>
</div>
